/**
 * UCC Jurisdictions Hooks (admin-portal)
 *
 * React Query hooks for jurisdiction rules and fee calculation,
 * scoped to the FI-tenant operator view. Mirrors the platform-admin
 * useUccJurisdictions.ts pattern but imports from the admin-portal
 * typed client layer.
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import type { components } from '@aaimplatform/institutional-types/ucc-jurisdiction-rules';
import { uccJurisdictionRulesClient } from '../api/client';

function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error || !result.data) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    const errorBody = result.error as { message?: string } | undefined;
    throw new Error(errorBody?.message ?? `HTTP ${status}: ${statusText}`);
  }
  return result.data;
}

export function useJurisdictions() {
  return useQuery({
    queryKey: ['uccJurisdictions'],
    queryFn: async () => {
      const result = await uccJurisdictionRulesClient.GET('/jurisdictions');
      return unwrap(result);
    },
  });
}

export function useJurisdictionFeeSchedule(stateCode: string | undefined) {
  return useQuery({
    queryKey: ['uccJurisdictionFees', stateCode],
    queryFn: async () => {
      const result = await uccJurisdictionRulesClient.GET('/jurisdictions/{stateCode}/fee-schedule', {
        params: { path: { stateCode: stateCode! } },
      });
      return unwrap(result);
    },
    enabled: !!stateCode,
  });
}

export function useCalculateFee() {
  return useMutation({
    mutationFn: async ({
      stateCode,
      body,
    }: {
      stateCode: string;
      body: components['schemas']['FeeCalculationRequest'];
    }) => {
      const result = await uccJurisdictionRulesClient.POST('/jurisdictions/{stateCode}/calculate-fee', {
        params: { path: { stateCode } },
        body,
      });
      return unwrap(result);
    },
  });
}
